import { useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/features/auth/AuthContext"
import { useAdminAccess } from "@/features/admin/useAdminAccess"
import { ButtonLink } from "@/lib/ButtonLink"
import { PORTAL_ADMIN, PORTAL_DEPOSIT, PORTAL_EVENTS, PORTAL_PROJECTS, PUBLIC_DEPOSITS } from "@/app/router/portalRoutes"
import { isDepositsUiEnabled } from "@/features/deposits/depositsUiEnabled"

type FooterLink = { labelKey: string; path: string; openInNewTab?: boolean }

type FooterColumn = {
  titleKey: "footer.portalTitle" | "footer.infoTitle"
  links: FooterLink[]
}

type FooterSocialItem = {
  id: "vk" | "telegram"
  href: string
  remixIcon: string
  labelKey: "footer.socialVk" | "footer.socialTelegram"
}

const footerSocialItems: FooterSocialItem[] = [
  { id: "vk", href: "https://vk.ru/leninkaart", remixIcon: "ri-vk-fill", labelKey: "footer.socialVk" },
  {
    id: "telegram",
    href: "https://t.me/leninkaart",
    remixIcon: "ri-telegram-fill",
    labelKey: "footer.socialTelegram",
  },
]

const infoColumn: FooterColumn = {
  titleKey: "footer.infoTitle",
  links: [
    { labelKey: "footer.aboutLibrary", path: "/about" },
    { labelKey: "footer.contacts", path: "/contacts" },
    { labelKey: "footer.terms", path: "/help/agreement", openInNewTab: true },
    { labelKey: "footer.personalDataConsent", path: "/help/personal", openInNewTab: true },
    { labelKey: "footer.personalDataProcessingPolicy", path: "/help/privacy", openInNewTab: true },
  ],
}

export default function Footer() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { isAuthenticated } = useAuth()
  const { hasAnyAdminAccess } = useAdminAccess()
  const brandMarkLabel = `${t("brand.leninka")} ${t("brand.artIp")}`

  const handleFooterHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (pathname !== "/") {
      return
    }
    e.preventDefault()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const portalColumn = useMemo((): FooterColumn => {
    const links: FooterLink[] = [
      { labelKey: "nav.home", path: "/" },
      { labelKey: "nav.events", path: isAuthenticated ? PORTAL_EVENTS : "/events" },
    ]
    if (isAuthenticated) {
      links.push({ labelKey: "nav.projects", path: PORTAL_PROJECTS })
    }
    if (isDepositsUiEnabled) {
      links.push({ labelKey: "nav.deposit", path: isAuthenticated ? PORTAL_DEPOSIT : PUBLIC_DEPOSITS })
    }
    if (isAuthenticated && hasAnyAdminAccess) {
      links.push({ labelKey: "nav.admin", path: PORTAL_ADMIN })
    }
    return { titleKey: "footer.portalTitle", links }
  }, [isAuthenticated, hasAnyAdminAccess])

  return (
    <footer className="bg-[#F5E6D3]/40 border-t border-brand-500/10 w-full min-w-0">
      <div className="max-w-[1400px] mx-auto w-full min-w-0 px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 w-full min-w-0">
          <div className="col-span-2 order-3 min-w-0 lg:order-1 lg:col-span-2">
            <Link
              to="/"
              className="flex flex-wrap items-center gap-3 mb-5 min-w-0"
              aria-label={brandMarkLabel}
              onClick={handleFooterHomeClick}
            >
              <div className="relative w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center flex-shrink-0" aria-hidden>
                <svg
                  viewBox="0 0 36 36"
                  className="w-8 h-8 sm:w-9 sm:h-9"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="14" cy="18" r="10" className="fill-neutral-900" />
                  <circle cx="24.5" cy="22" r="6.5" className="fill-[#801818]" />
                </svg>
              </div>
              <div className="flex min-w-0 flex-1 flex-col leading-none">
                <div className="flex items-baseline gap-x-1 gap-y-0.5 min-w-0 flex-wrap">
                  <span className="text-lg font-black tracking-tight text-gray-900">{t("brand.leninka")}</span>
                  <span className="text-lg font-black tracking-tight text-brand-500">{t("brand.artIp")}</span>
                </div>
                <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-gray-400 break-words hyphens-auto">
                  {t("brand.digitalEnv")}
                </span>
              </div>
            </Link>
            <p className="text-sm text-gray-500 leading-relaxed max-w-full sm:max-w-xs break-words">
              {t("footer.tagline")}
            </p>
            <div className="flex items-center gap-3 mt-6">
              {footerSocialItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-500 text-white hover:bg-brand-700 transition-colors cursor-pointer"
                  aria-label={`${t(item.labelKey)}, ${t("common.opensInNewTab")}`}
                >
                  <i className={`${item.remixIcon} text-sm`} aria-hidden />
                </a>
              ))}
            </div>
          </div>

          {[portalColumn, infoColumn].map((col, idx) => (
            <div
              key={col.titleKey}
              className={`min-w-0 ${idx === 0 ? "order-1 lg:order-2" : "order-2 lg:order-3"}`}
            >
              <h4 className="text-xs font-bold uppercase tracking-widest text-brand-500 mb-4">
                {t(col.titleKey)}
              </h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={`${col.titleKey}-${link.labelKey}-${link.path}`}>
                    <ButtonLink
                      to={link.path}
                      variant="footerText"
                      onClick={link.path === "/" ? handleFooterHomeClick : undefined}
                      {...(link.openInNewTab
                        ? {
                            target: "_blank",
                            rel: "noopener noreferrer",
                            "aria-label": `${t(link.labelKey)}, ${t("common.opensInNewTab")}`,
                          }
                        : {})}
                    >
                      {t(link.labelKey)}
                    </ButtonLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-gray-200 w-full min-w-0 text-center sm:text-left">
          <p className="text-xs text-gray-400 break-words">{t("footer.copyright")}</p>
        </div>
      </div>
    </footer>
  )
}
